import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, InputBase } from "@mui/material";
import config from "../../config";
import {
  COLORS,
  DISPLAY_FONT,
  BODY_FONT,
  PRIMARY_BTN_SX,
  GHOST_BTN_SX,
  SHADOW_SM,
  focusInput,
  ensureMandiFontsLoaded,
  MandiGlobalStyles,
  ScreenHeader,
  ScreenFooter,
  TileInputRow,
  FlatAutocomplete,
  SummaryRow,
  SelectableTile,
  ArrivalMeterCard,
  MismatchWarningBanner,
  DuplicateReportBanner,
  EmptyStateCard,
  BrandLogo,
  MandiCodeSearchField,
  PartnerDetailConfirmCard,
} from "./mandiDesignSystem";
const ARRIVAL_TOLERANCE_PCT = 0.05;

const OTHER_VARIETY = "अन्य";
const SUBMIT_ENDPOINT_PATH = "/campaign-call/manual-ivr-form";
const PARTNER_BY_CODE_ENDPOINT_PATH = "/mandi-partner/get-partners-by-code";
const BUYERS_BY_VARIETY_ENDPOINT_PATH = "/buyers/buyer/get";
const HTTP_CONFLICT = 409;

const SCREEN_ORDER = ["partner", "total", "select", "variety", "preview", "done"];

const HEAD = {
  partner: ["मंडी पार्टनर", "SELECT MANDI PARTNER"],
  total: ["कुल आवक", "MARKET ARRIVAL"],
  select: ["वैरायटी चुनें", "SELECT VARIETIES"],
  variety: ["वैरायटी की जानकारी", "VARIETY DETAILS"],
  preview: ["समीक्षा करें", "REVIEW & CONFIRM"],
  done: ["हो गया", "SUBMITTED"],
};

const DAYS = ["रविवार", "सोमवार", "मंगलवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार"];
const MONTHS = ["जन", "फ़र", "मार्च", "अप्रैल", "मई", "जून", "जुलाई", "अग", "सित", "अक्टू", "नव", "दिस"];

const AUTH_TOKEN_STORAGE_KEY = "token";

const getAuthHeaders = () => {
  const token = sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY);

  if (!token) {
    throw new Error("Authentication token missing");
  }

  return {
    Authorization: `Bearer ${token}`,
  };
};

const onlyDigits = (val) => (val || "").replace(/[^0-9]/g, "");

const makeEmptyVarietyData = () => ({
  customName: "",
  total: "",
  handQty: "",
  handLo: "",
  handHi: "",
  combQty: "",
  combLo: "",
  combHi: "",
  buyers: [{ selection: "", bags: "", buyerId: "", bagsAuto: false }],
});

const computeVarietyTotal = (d) => {
  if (!d) return 0;
  const explicit = Number(d.total);
  if (explicit) return explicit;
  return (Number(d.handQty) || 0) + (Number(d.combQty) || 0);
};

const computeDisplayedBuyers = (buyers, total) => {
  let running = 0;
  return (buyers || []).map((b, idx) => {
    if (idx === 0 || b.bagsAuto === false) {
      running += Number(b.bags) || 0;
      return b;
    }
    const remaining = Math.max((Number(total) || 0) - running, 0);
    running += remaining;
    return { ...b, bags: String(remaining) };
  });
};

const getVarietyDisplayName = (varietyKey, d) =>
  varietyKey === OTHER_VARIETY ? (d?.customName || "").trim() || OTHER_VARIETY : varietyKey;

const getBuyerName = (b) => {
  if (!b) return "";
  return (b.selection || "").trim();
};

const getBuyerId = (b) => {
  if (!b) return "";
  return b.buyerId || "";
};

const getRateError = (lo, hi) => {
  if (lo === "" || hi === "") return "";
  const loNum = Number(lo);
  const hiNum = Number(hi);
  if (loNum >= hiNum) {
    return "लो रेट, हाई रेट से कम होना चाहिए";
  }
  return "";
};

const parseVarietyResponse = (json) => {
  const list = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];

  const entries = list
    .filter((item) => typeof item === "string" || item?.status !== false)
    .map((item) => {
      if (typeof item === "string") {
        return { name: item.trim(), id: "" };
      }
      const name = String(item?.varietyName || "").trim();
      const id = String(item?._id || item?.varietyId || item?.id || "");
      return { name, id };
    })
    .filter((entry) => entry.name);

  if (entries.length === 0) {
    return { names: [], idMap: {} };
  }

  const seen = new Map();
  entries.forEach((entry) => {
    if (!seen.has(entry.name)) seen.set(entry.name, entry.id);
  });

  const sortedNames = [...seen.keys()].sort((a, b) => a.localeCompare(b, "hi"));

  return {
    names: [...sortedNames, OTHER_VARIETY],
    idMap: Object.fromEntries(seen),
  };
};


const parseBuyerResponse = (json) => {
  const list = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];

  const entries = list
    .filter((item) => typeof item === "string" || item?.status !== false)
    .map((item) => {
      if (typeof item === "string") {
        return { name: item.trim(), id: "" };
      }
      const name = String(item?.buyerName || item?.name || "").trim();
      const id = String(item?._id || item?.buyerId || item?.id || "");
      return { name, id };
    })
    .filter((entry) => entry.name);

  const seen = new Map();
  entries.forEach((entry) => {
    if (!seen.has(entry.name)) seen.set(entry.name, entry.id);
  });

  return [...seen.entries()]
    .map(([name, id]) => ({ name, id }))
    .sort((a, b) => a.name.localeCompare(b.name, "hi"));
};

const normalizeMandiPartnerDetails = (item) => {
  if (!item) return null;

  const id = item._id || item.mandiPartnerId || item.id || "";
  const partnerName = (item.businessName || "").trim() || (item.mandiPartner || "").trim();
  const location = (item.mandiLocation || "").trim();
  const phone = (item.contactNumber || "").trim();

  return {
    id: String(id),
    partnerName,
    location,
    phone,
  };
};

const fetchMandiPartnerByCode = async (mandiCode) => {
  const res = await fetch(
    `${config.apiBaseUrl}${PARTNER_BY_CODE_ENDPOINT_PATH}?mandiCode=${encodeURIComponent(mandiCode)}`,
    { headers: { ...getAuthHeaders() } }
  );

  if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
  const json = await res.json();

  if (!json?.success || !json?.mandiPartner) {
    throw new Error(json?.message || "Partner not found");
  }

  return json.mandiPartner;
};

const fetchBuyersByVarietyId = async (varietyId) => {
  const res = await fetch(
    `${config.apiBaseUrl}${BUYERS_BY_VARIETY_ENDPOINT_PATH}?varietyId=${encodeURIComponent(varietyId)}`,
    { headers: { ...getAuthHeaders() } }
  );

  if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

  return res.json();
};

export default function MandiReportForm() {
  const appRef = useRef(null);
  const historyRef = useRef([]);
  const fetchedVarietiesRef = useRef(false);
  const partnerConfirmCardRef = useRef(null);
  const [screen, setScreen] = useState("partner");
  const [market, setMarket] = useState("");
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState({});
  const [vIdx, setVIdx] = useState(0);
  const [mandiCode, setMandiCode] = useState("");
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [selectedPartnerDetails, setSelectedPartnerDetails] = useState(null);
  const [partnerConfirmed, setPartnerConfirmed] = useState(false);
  const [searchingPartner, setSearchingPartner] = useState(false);
  const [partnerSearchError, setPartnerSearchError] = useState("");
  const [varietyOptions, setVarietyOptions] = useState([]);
  const [varietyIdMap, setVarietyIdMap] = useState({});
  const [varietiesLoading, setVarietiesLoading] = useState(true);
  const [varietiesError, setVarietiesError] = useState(false);
  const [buyersByVariety, setBuyersByVariety] = useState({});

  const loadVarieties = async () => {
    try {
      setVarietiesLoading(true);
      setVarietiesError(false);
      const res = await fetch(`${config.apiBaseUrl}/variety/get`, {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const json = await res.json();
      const { names, idMap } = parseVarietyResponse(json);
      setVarietyOptions(names);
      setVarietyIdMap(idMap);
    } catch (err) {
      console.error("Fetch varieties error:", err);
      setVarietiesError(true);
      setVarietyOptions([]);
      setVarietyIdMap({});
    } finally {
      setVarietiesLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedVarietiesRef.current) return;
    fetchedVarietiesRef.current = true;
    loadVarieties();
  }, []);

  const loadBuyersForVariety = useCallback(
    async (varietyKey) => {
      const varietyId = varietyIdMap[varietyKey];
      if (varietyKey === OTHER_VARIETY || !varietyId) {
        setBuyersByVariety((prev) => ({
          ...prev,
          [varietyKey]: { options: [], idByName: {}, loading: false, error: false },
        }));
        return;
      }

      setBuyersByVariety((prev) => ({
        ...prev,
        [varietyKey]: {
          options: prev[varietyKey]?.options || [],
          idByName: prev[varietyKey]?.idByName || {},
          loading: true,
          error: false,
        },
      }));

      try {
        const json = await fetchBuyersByVarietyId(varietyId);
        const buyers = parseBuyerResponse(json);
        const idByName = Object.fromEntries(buyers.map((b) => [b.name, b.id]));
        setBuyersByVariety((prev) => ({
          ...prev,
          [varietyKey]: {
            options: buyers.map((b) => b.name),
            idByName,
            loading: false,
            error: false,
          },
        }));
      } catch (err) {
        console.error("Fetch buyers by variety error:", err);
        setBuyersByVariety((prev) => ({
          ...prev,
          [varietyKey]: { options: [], idByName: {}, loading: false, error: true },
        }));
      }
    },
    [varietyIdMap]
  );

  const supplier = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      id: params.get("id") || params.get("supplier_id") || "",
      name: params.get("name") || params.get("supplier_name") || "",
    };
  }, []);

  const todayLabel = useMemo(() => {
    const d = new Date();
    return `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  }, []);

  useEffect(() => {
    ensureMandiFontsLoaded();
  }, []);

  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty("--app-vh", `${window.innerHeight * 0.01}px`);
    };
    setVh();
    window.addEventListener("resize", setVh);
    window.addEventListener("orientationchange", setVh);
    window.visualViewport?.addEventListener("resize", setVh);
    return () => {
      window.removeEventListener("resize", setVh);
      window.removeEventListener("orientationchange", setVh);
      window.visualViewport?.removeEventListener("resize", setVh);
    };
  }, []);

  useEffect(() => {
    if (!selectedPartnerDetails || !partnerConfirmCardRef.current) return undefined;

    const timer = setTimeout(() => {
      partnerConfirmCardRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 120);

    return () => clearTimeout(timer);
  }, [selectedPartnerDetails]);

  const goTo = (next) => {
    historyRef.current.push(screen);
    setScreen(next);
  };

  const goBack = () => {
    if (screen === "variety" && vIdx > 0) {
      setVIdx((i) => i - 1);
      return;
    }
    const prev = historyRef.current.pop() || "partner";
    setScreen(prev);
  };

  const handleToSelect = () => goTo("select");

  const handleMandiCodeChange = (value) => {
    const digits = onlyDigits(value);
    setMandiCode(digits);
    setSelectedPartnerDetails(null);
    setSelectedPartnerId("");
    setPartnerConfirmed(false);
    setPartnerSearchError("");
  };

  const handleSearchPartner = async () => {
    const code = mandiCode.trim();
    if (!code || searchingPartner) return;

    setSearchingPartner(true);
    setPartnerSearchError("");
    setPartnerConfirmed(false);

    try {
      const partner = await fetchMandiPartnerByCode(code);
      const details = normalizeMandiPartnerDetails(partner);
      setSelectedPartnerDetails(details);
      setSelectedPartnerId(details?.id || "");
    } catch (err) {
      console.error("Fetch mandi partner by code error:", err);
      setSelectedPartnerDetails(null);
      setSelectedPartnerId("");
      setPartnerSearchError("यह मंडी कोड सही नहीं है या पार्टनर नहीं मिला। कृपया दोबारा कोशिश करें।");
    } finally {
      setSearchingPartner(false);
    }
  };

  const toggleVariety = (variety) => {
    setSelected((prev) =>
      prev.includes(variety) ? prev.filter((v) => v !== variety) : [...prev, variety]
    );
    setData((prev) => (prev[variety] ? prev : { ...prev, [variety]: makeEmptyVarietyData() }));
  };

  const { totalMarket, totalSum, meterPct, diff, diffPercent, isOk } = useMemo(() => {
    const totalMarketVal = Number(market) || 0;
    const sum = selected.reduce((acc, v) => acc + computeVarietyTotal(data[v]), 0);
    const tolerance = Math.max(totalMarketVal * ARRIVAL_TOLERANCE_PCT, 1);
    const differenceVal = Math.abs(totalMarketVal - sum);
    const pct = totalMarketVal ? Math.min(100, (sum / totalMarketVal) * 100) : 0;
    const differencePct = totalMarketVal ? (differenceVal / totalMarketVal) * 100 : 0;
    return {
      totalMarket: totalMarketVal,
      totalSum: sum,
      meterPct: pct,
      diff: differenceVal,
      diffPercent: differencePct,
      isOk: totalMarketVal > 0 && differenceVal <= tolerance,
    };
  }, [market, selected, data]);

  const isMarketTotalValid = market !== "" && Number(market) > 0;

  const startVarieties = () => {
    if (selected.length === 0) return;
    setVIdx(0);
    goTo("variety");
  };

  const updateVarietyField = (variety, field, rawValue) => {
    setData((prev) => ({
      ...prev,
      [variety]: { ...(prev[variety] || makeEmptyVarietyData()), [field]: onlyDigits(rawValue) },
    }));
  };

  const updateVarietyRawField = (variety, field, rawValue) => {
    setData((prev) => ({
      ...prev,
      [variety]: { ...(prev[variety] || makeEmptyVarietyData()), [field]: rawValue },
    }));
  };

  const setBuyerField = (variety, index, key, rawValue) => {
    setData((prev) => {
      const current = prev[variety] || makeEmptyVarietyData();
      const buyers = current.buyers.map((b, i) => {
        if (i !== index) return b;
        if (key === "bags") return { ...b, bags: onlyDigits(rawValue), bagsAuto: false };
        if (key === "selection") {
          const buyerId = buyersByVariety[variety]?.idByName?.[rawValue] || "";
          return {
            ...b,
            selection: rawValue,
            buyerId,
          };
        }
        return b;
      });
      return { ...prev, [variety]: { ...current, buyers } };
    });
  };

  const addBuyerRow = (variety) => {
    setData((prev) => {
      const current = prev[variety] || makeEmptyVarietyData();
      return {
        ...prev,
        [variety]: { ...current, buyers: [...current.buyers, { selection: "", bags: "", buyerId: "", bagsAuto: true }] },
      };
    });
  };

  const removeBuyerRow = (variety, index) => {
    setData((prev) => {
      const current = prev[variety] || makeEmptyVarietyData();
      if (current.buyers.length <= 1) return prev;
      return { ...prev, [variety]: { ...current, buyers: current.buyers.filter((_, i) => i !== index) } };
    });
  };

  const nextVariety = () => {
    if (vIdx < selected.length - 1) {
      setVIdx((i) => i + 1);
    } else {
      goTo("preview");
    }
  };

  const buildPayload = () => ({
    mandiPartnerName: selectedPartnerId || supplier.id || "",
    date: new Date().toISOString().slice(0, 10),
    marketTotal: Number(market) || 0,
    varieties: selected.map((v) => {
      const d = data[v] || makeEmptyVarietyData();
      const varietyTotal = computeVarietyTotal(d);
      const finalBuyers = computeDisplayedBuyers(d.buyers, varietyTotal);
      return {
        varietyId: varietyIdMap[v] || "",
        variety: getVarietyDisplayName(v, d),
        total: varietyTotal,
        handQty: Number(d.handQty) || 0,
        handLo: Number(d.handLo) || 0,
        handHi: Number(d.handHi) || 0,
        combQty: Number(d.combQty) || 0,
        combLo: Number(d.combLo) || 0,
        combHi: Number(d.combHi) || 0,
        buyers: (finalBuyers || [])
          .map((b) => ({ buyerId: getBuyerId(b), name: getBuyerName(b), bags: Number(b.bags) || 0 }))
          .filter((b) => b.name),
      };
    }),
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [duplicateSubmission, setDuplicateSubmission] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState("");

  const submitReport = async () => {
    if (!isOk || submitting || duplicateSubmission) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${config.apiBaseUrl}${SUBMIT_ENDPOINT_PATH}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(buildPayload()),
      });

      if (res.status === HTTP_CONFLICT) {
        const json = await res.json().catch(() => null);
        setDuplicateMessage(
          json?.message
            ? "इस मंडी पार्टनर के लिए आज की तारीख़ की रिपोर्ट पहले से ही भेजी जा चुकी है।"
            : ""
        );
        setDuplicateSubmission(true);
        return;
      }

      const json = await res.json().catch(() => null);
      if (!res.ok || json?.success === false) {
        throw new Error(json?.message || `Request failed with status ${res.status}`);
      }
      goTo("done");
    } catch (err) {
      // console.error("Submit report error:", err);
      setSubmitError("रिपोर्ट भेजने में समस्या हुई। कृपया दोबारा कोशिश करें।");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    historyRef.current = [];
    setMarket("");
    setSelected([]);
    setData({});
    setVIdx(0);
    setMandiCode("");
    setSelectedPartnerId("");
    setSelectedPartnerDetails(null);
    setPartnerConfirmed(false);
    setSearchingPartner(false);
    setPartnerSearchError("");
    setSubmitError("");
    setDuplicateSubmission(false);
    setDuplicateMessage("");
    setBuyersByVariety({});
    setScreen("partner");
  };

  const editVariety = (idx) => {
    setVIdx(idx);
    goTo("variety");
  };

  const editMarketTotal = () => goTo("total");

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const target = e.target;
    if (target.tagName !== "INPUT") return;
    e.preventDefault();
    const root = appRef.current;
    if (!root) return;
    const inputs = Array.from(root.querySelectorAll("input")).filter(
      (el) => el.offsetParent !== null
    );
    const idx = inputs.indexOf(target);
    if (idx > -1 && idx < inputs.length - 1) {
      inputs[idx + 1].focus();
    } else {
      target.blur();
      if (screen === "total" && isMarketTotalValid) handleToSelect();
    }
  };

  const screenIndex = SCREEN_ORDER.indexOf(screen);
  const [title, subtitle] = HEAD[screen];
  const showBack = screen !== "partner" && screen !== "done";
  const currentVariety = selected[vIdx];
  const currentData = data[currentVariety] || makeEmptyVarietyData();

  useEffect(() => {
    if (screen !== "variety" || !currentVariety) return;
    if (varietiesLoading) return;
    if (buyersByVariety[currentVariety]) return;
    loadBuyersForVariety(currentVariety);
  }, [screen, currentVariety, varietiesLoading, buyersByVariety, loadBuyersForVariety]);

  const currentBuyerState = buyersByVariety[currentVariety];
  const currentBuyerOptions = currentBuyerState?.options || [];
  const buyersLoadingForCurrentVariety = Boolean(currentBuyerState?.loading);

  const currentVarietyTotal = computeVarietyTotal(currentData);
  const displayedBuyers = useMemo(
    () => computeDisplayedBuyers(currentData.buyers, currentVarietyTotal),
    [currentData.buyers, currentVarietyTotal]
  );

  const handRateError = useMemo(
    () => getRateError(currentData.handLo, currentData.handHi),
    [currentData.handLo, currentData.handHi]
  );
  const combRateError = useMemo(
    () => getRateError(currentData.combLo, currentData.combHi),
    [currentData.combLo, currentData.combHi]
  );
  const hasRateError = Boolean(handRateError || combRateError);
  const canProceedFromPartner = Boolean(selectedPartnerDetails) && partnerConfirmed && !searchingPartner;
  const greetingName = selectedPartnerDetails?.partnerName || supplier.name;

  return (
    <>
      <MandiGlobalStyles />

      <Box
        ref={appRef}
        onKeyDown={handleKeyDown}
        lang="hi"
        sx={{
          maxWidth: 460,
          mx: "auto",
          height: "calc(var(--app-vh, 1vh) * 100)",
          "@supports (height: 100dvh)": {
            height: "100dvh",
          },
          display: "flex",
          flexDirection: "column",
          background: COLORS.page,
          position: "relative",
          overflow: "hidden",
          fontFamily: BODY_FONT,
          color: COLORS.ink,
          "*": { boxSizing: "border-box" },
        }}
      >
        <ScreenHeader
          title={title}
          subtitle={subtitle}
          showBack={showBack}
          onBack={goBack}
          stepCount={SCREEN_ORDER.length}
          activeStep={screenIndex}
        />

        <Box sx={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          <Box
            key={screen}
            sx={{ padding: "18px 16px 120px", animation: "mandiSlide .32s cubic-bezier(.2,.7,.3,1)" }}
          >
            {screen === "partner" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  pt: "26px",
                  "@media (max-height: 700px)": { pt: "10px" },
                }}
              >
                <BrandLogo />
                <Box
                  component="h2"
                  sx={{
                    fontFamily: DISPLAY_FONT,
                    fontWeight: 800,
                    fontSize: 22,
                    margin: "0 0 6px",
                    "@media (max-height: 700px)": { fontSize: 17, margin: "0 0 3px" },
                  }}
                >
                  अपना मंडी कोड डालें
                </Box>
                <Box
                  component="p"
                  sx={{
                    color: COLORS.muted,
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    margin: "0 8px 20px",
                    "@media (max-height: 700px)": { fontSize: 12, margin: "0 8px 10px" },
                  }}
                >
                  अपना मंडी पार्टनर कोड डालें और "खोजें" बटन दबाएँ
                </Box>

                <Box sx={{ width: "100%", textAlign: "left" }}>
                  <MandiCodeSearchField
                    value={mandiCode}
                    onChange={handleMandiCodeChange}
                    onSearch={handleSearchPartner}
                    loading={searchingPartner}
                  />

                  {partnerSearchError && (
                    <Box sx={{ mt: "14px" }}>
                      <EmptyStateCard
                        message={partnerSearchError}
                        actionLabel="फिर कोशिश करें"
                        onAction={handleSearchPartner}
                      />
                    </Box>
                  )}

                  {selectedPartnerDetails && !partnerSearchError && (
                    <Box ref={partnerConfirmCardRef}>
                      <PartnerDetailConfirmCard
                        name={selectedPartnerDetails.partnerName}
                        location={selectedPartnerDetails.location}
                        phone={selectedPartnerDetails.phone}
                        checked={partnerConfirmed}
                        onToggle={setPartnerConfirmed}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {screen === "total" && (
              <Box sx={{ mt: "6px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    mb: "16px",
                  }}
                >
                  <Box>
                    <Box component="h2" sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: 19, margin: 0 }}>
                      नमस्ते,{" "}
                      <Box component="span" sx={{ color: COLORS.emerald }}>
                        {greetingName ? `${greetingName} जी` : "जी"}
                      </Box>
                    </Box>
                    <Box sx={{ color: COLORS.muted, fontWeight: 600, fontSize: 12.5, mt: "3px" }}>
                      {todayLabel}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 21, lineHeight: 1.25, mb: "4px" }}>
                  आज पूरी मंडी में कुल आवक?
                </Box>
                <Box sx={{ color: COLORS.muted, fontSize: 13, mb: "16px" }}>
                  Total market arrival — पूरी मंडी का अनुमान
                </Box>
                <Box
                  sx={{
                    background: COLORS.card,
                    border: `2px solid ${COLORS.line}`,
                    borderRadius: "18px",
                    boxShadow: SHADOW_SM,
                    padding: "14px 18px",
                    display: "flex",
                    alignItems: "baseline",
                    gap: "8px",
                    transition: "border-color .2s",
                    "&:focus-within": { borderColor: COLORS.emerald },
                  }}
                >
                  <InputBase
                    autoFocus
                    value={market}
                    onChange={(e) => setMarket(onlyDigits(e.target.value))}
                    placeholder="0"
                    inputProps={{ inputMode: "numeric", enterKeyHint: "next" }}
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      fontWeight: 700,
                      fontSize: 40,
                      color: COLORS.emerald,
                      width: "100%",
                      "& input": { padding: 0 },
                      "& input::placeholder": { color: "#CBD3CB", opacity: 1 },
                    }}
                  />
                  <Box component="span" sx={{ fontWeight: 600, color: COLORS.muted, fontSize: 16, whiteSpace: "nowrap" }}>
                    बोरी
                  </Box>
                </Box>
                {!isMarketTotalValid && (
                  <Box sx={{ fontSize: 12, color: COLORS.amberWarn, fontWeight: 600, mt: "10px" }}>
                    आगे बढ़ने के लिए कुल आवक दर्ज करें
                  </Box>
                )}
              </Box>
            )}

            {screen === "select" && (
              <>
                <ArrivalMeterCard
                  totalMarket={totalMarket}
                  totalSum={totalSum}
                  meterPct={meterPct}
                  isOk={isOk}
                  diff={diff}
                  diffPercent={diffPercent}
                />

                <Box sx={{ mt: "6px" }}>
                  <Box sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 19 }}>
                    आज कौन सी वैरायटी आईं?
                  </Box>
                  <Box sx={{ color: COLORS.muted, fontSize: 13, mb: "16px" }}>
                    जितनी आईं सब चुनें — हर एक की अलग स्क्रीन खुलेगी
                    {varietiesLoading ? " · सूची लोड हो रही है…" : ""}
                  </Box>
                </Box>

                {varietiesLoading && (
                  <Box sx={{ color: COLORS.muted, fontSize: 14, textAlign: "center", py: "20px" }}>
                    लोड हो रहा है…
                  </Box>
                )}

                {!varietiesLoading && varietyOptions.length === 0 && (
                  <EmptyStateCard
                    message={
                      varietiesError
                        ? "वैरायटी लोड करने में समस्या हुई।"
                        : "कोई वैरायटी उपलब्ध नहीं है।"
                    }
                    actionLabel="फिर कोशिश करें"
                    onAction={loadVarieties}
                  />
                )}

                {!varietiesLoading && varietyOptions.length > 0 && (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    {varietyOptions.map((v) => (
                      <SelectableTile
                        key={v}
                        label={v}
                        selected={selected.includes(v)}
                        onClick={() => toggleVariety(v)}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}

            {screen === "variety" && currentVariety && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "linear-gradient(120deg,#FFF7E6,#FFFDF7)",
                    border: `1px solid ${COLORS.line}`,
                    borderRadius: "16px",
                    padding: "12px 16px",
                    mb: "14px",
                    boxShadow: SHADOW_SM,
                    gap: "10px",
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      fontWeight: 800,
                      fontSize: 24,
                      color: COLORS.emerald,
                      display: "flex",
                      alignItems: "center",
                      gap: "9px",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    🌾{" "}
                    {currentVariety === OTHER_VARIETY ? (
                      <InputBase
                        autoFocus
                        value={currentData.customName}
                        onChange={(e) => updateVarietyRawField(currentVariety, "customName", e.target.value)}
                        placeholder="वैरायटी का नाम लिखें"
                        sx={{
                          fontFamily: DISPLAY_FONT,
                          fontWeight: 800,
                          fontSize: 22,
                          color: COLORS.emerald,
                          width: "100%",
                          "& input": { padding: 0 },
                          "& input::placeholder": { color: "#CBD3CB", opacity: 1 },
                        }}
                      />
                    ) : (
                      <span>{currentVariety}</span>
                    )}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      fontWeight: 700,
                      color: COLORS.goldDeep,
                      fontSize: 16,
                      background: "#FFF1D6",
                      padding: "3px 12px",
                      borderRadius: "20px",
                      flex: "none",
                    }}
                  >
                    {vIdx + 1} / {selected.length}
                  </Box>
                </Box>

                {/* Total */}
                <Box
                  sx={{
                    background: COLORS.card,
                    border: `1px solid ${COLORS.line}`,
                    borderRadius: "18px",
                    padding: "13px 13px 15px",
                    mb: "13px",
                    boxShadow: SHADOW_SM,
                  }}
                >
                  <Box
                    onClick={focusInput}
                    sx={{
                      background: COLORS.page,
                      border: "2px solid transparent",
                      borderRadius: "13px",
                      padding: "9px 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      cursor: "text",
                      transition: "border-color .2s",
                      "&:focus-within": { borderColor: COLORS.emerald, background: "#fff" },
                    }}
                  >
                    <Box
                      component="label"
                      sx={{ fontSize: 11, color: COLORS.muted, fontWeight: 600, lineHeight: 1.2, flex: "none" }}
                    >
                      कुल आवक • Total arrival
                    </Box>
                    <InputBase
                      value={currentData.total}
                      onChange={(e) => updateVarietyField(currentVariety, "total", e.target.value)}
                      placeholder="0"
                      inputProps={{ inputMode: "numeric", enterKeyHint: "next" }}
                      sx={{
                        fontFamily: DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: 28,
                        color: COLORS.emerald,
                        width: "auto",
                        flex: 1,
                        "& input": { padding: "2px 0 0", textAlign: "right" },
                        "& input::placeholder": { color: "#D2DAD2", opacity: 1 },
                      }}
                    />
                    <Box component="span" sx={{ fontSize: 12, color: COLORS.muted, fontWeight: 600, whiteSpace: "nowrap" }}>
                      बोरी
                    </Box>
                  </Box>
                </Box>

                {/* Hand */}
                <Box
                  sx={{
                    background: COLORS.card,
                    border: `1px solid ${COLORS.line}`,
                    borderRadius: "18px",
                    padding: "13px 13px 15px",
                    mb: "13px",
                    boxShadow: SHADOW_SM,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      fontWeight: 700,
                      fontSize: 15,
                      color: COLORS.emeraldDark,
                      margin: "0 2px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "7px",
                    }}
                  >
                    ✋ हाथ{" "}
                    <Box component="span" sx={{ color: COLORS.muted, fontFamily: BODY_FONT, fontWeight: 600, fontSize: 12 }}>
                      Hand
                    </Box>
                  </Box>
                  <TileInputRow
                    qty={currentData.handQty}
                    lo={currentData.handLo}
                    hi={currentData.handHi}
                    onQtyChange={(e) => updateVarietyField(currentVariety, "handQty", e.target.value)}
                    onLoChange={(e) => updateVarietyField(currentVariety, "handLo", e.target.value)}
                    onHiChange={(e) => updateVarietyField(currentVariety, "handHi", e.target.value)}
                    error={handRateError}
                  />
                </Box>

                {/* Combine */}
                <Box
                  sx={{
                    background: COLORS.card,
                    border: `1px solid ${COLORS.line}`,
                    borderRadius: "18px",
                    padding: "13px 13px 15px",
                    mb: "13px",
                    boxShadow: SHADOW_SM,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      fontWeight: 700,
                      fontSize: 15,
                      color: COLORS.emeraldDark,
                      margin: "0 2px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "7px",
                    }}
                  >
                    🚜 कंबाइन{" "}
                    <Box component="span" sx={{ color: COLORS.muted, fontFamily: BODY_FONT, fontWeight: 600, fontSize: 12 }}>
                      Combine
                    </Box>
                  </Box>
                  <TileInputRow
                    qty={currentData.combQty}
                    lo={currentData.combLo}
                    hi={currentData.combHi}
                    onQtyChange={(e) => updateVarietyField(currentVariety, "combQty", e.target.value)}
                    onLoChange={(e) => updateVarietyField(currentVariety, "combLo", e.target.value)}
                    onHiChange={(e) => updateVarietyField(currentVariety, "combHi", e.target.value)}
                    error={combRateError}
                  />
                </Box>

                {/* Buyers */}
                <Box
                  sx={{
                    background: COLORS.card,
                    border: `1px solid ${COLORS.line}`,
                    borderRadius: "18px",
                    padding: "13px 13px 15px",
                    mb: "13px",
                    boxShadow: SHADOW_SM,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      fontWeight: 700,
                      fontSize: 15,
                      color: COLORS.emeraldDark,
                      margin: "0 2px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "7px",
                    }}
                  >
                    🏬 किसने खरीदा?{" "}
                    <Box component="span" sx={{ color: COLORS.muted, fontFamily: BODY_FONT, fontWeight: 600, fontSize: 12 }}>
                      Buyers · bags
                    </Box>
                  </Box>

                  {displayedBuyers.map((b, i) => {
                    const canRemove = currentData.buyers.length > 1;
                    const isAutoBags = i > 0 && b.bagsAuto !== false;
                    return (
                      <Box key={i} sx={{ display: "flex", gap: "9px", alignItems: "center", mb: "9px" }}>
                        <Box
                          onClick={focusInput}
                          sx={{
                            flex: 1.5,
                            minWidth: 0,
                            background: COLORS.page,
                            border: "2px solid transparent",
                            borderRadius: "13px",
                            padding: "4px 13px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            cursor: "text",
                            transition: ".2s",
                            "&:focus-within": { borderColor: COLORS.emerald, background: "#fff" },
                          }}
                        >
                          <FlatAutocomplete
                            options={currentBuyerOptions}
                            value={b.selection}
                            onChange={(val) => setBuyerField(currentVariety, i, "selection", val)}
                            placeholder={
                              buyersLoadingForCurrentVariety ? "खरीदार लोड हो रहे हैं…" : "खरीदार चुनें"
                            }
                          />
                        </Box>
                        <Box
                          onClick={focusInput}
                          sx={{
                            flex: 1,
                            background: COLORS.page,
                            border: "2px solid transparent",
                            borderRadius: "13px",
                            padding: "9px 13px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            justifyContent: "center",
                            cursor: "text",
                            transition: ".2s",
                            "&:focus-within": { borderColor: COLORS.emerald, background: "#fff" },
                          }}
                        >
                          <InputBase
                            value={b.bags}
                            onChange={(e) => setBuyerField(currentVariety, i, "bags", e.target.value)}
                            placeholder="0"
                            inputProps={{ inputMode: "numeric", enterKeyHint: "next" }}
                            sx={{
                              fontFamily: DISPLAY_FONT,
                              fontWeight: 700,
                              fontSize: 16,
                              color: COLORS.emerald,
                              width: "100%",
                              "& input": { padding: 0, textAlign: "right" },
                            }}
                          />
                          <Box component="small" sx={{ fontSize: 11, color: COLORS.muted, mt: "1px" }}>
                            {isAutoBags ? "बोरी • शेष" : "बोरी"}
                          </Box>
                        </Box>
                        {canRemove && (
                          <IconButton
                            size="small"
                            onClick={() => removeBuyerRow(currentVariety, i)}
                            sx={{ flex: "none", color: COLORS.muted, p: "4px" }}
                          >
                            🗑
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}

                  <Button
                    onClick={() => addBuyerRow(currentVariety)}
                    disableRipple
                    sx={{
                      mt: "2px",
                      alignSelf: "flex-start",
                      textTransform: "none",
                      fontFamily: DISPLAY_FONT,
                      fontWeight: 700,
                      fontSize: 14,
                      color: COLORS.emerald,
                      background: "transparent",
                      padding: "6px 4px",
                      minWidth: 0,
                      boxShadow: "none",
                      "&:hover": { background: "transparent", textDecoration: "underline", boxShadow: "none" },
                    }}
                  >
                    + और खरीदार जोड़ें
                  </Button>
                </Box>
              </>
            )}

            {screen === "preview" && (
              <Box sx={{ mt: "6px" }}>
                <Box sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 19, mb: "4px" }}>
                  अपनी रिपोर्ट जांच लें
                </Box>
                <Box sx={{ color: COLORS.muted, fontSize: 13, mb: "14px" }}>
                  भेजने से पहले एक बार देख लें — बदलाव के लिए किसी भी कार्ड पर टैप करें
                </Box>

                {duplicateSubmission && <DuplicateReportBanner message={duplicateMessage} />}

                {!duplicateSubmission && !isOk && (
                  <MismatchWarningBanner
                    totalMarket={totalMarket}
                    totalSum={totalSum}
                    diffPercent={diffPercent}
                  />
                )}

                <SummaryRow
                  label="पूरी मंडी आवक"
                  value={`${totalMarket.toLocaleString("en-IN")} बोरी`}
                  onClick={editMarketTotal}
                  editable
                />

                {selected.map((v, idx) => {
                  const d = data[v] || makeEmptyVarietyData();
                  const displayName = getVarietyDisplayName(v, d);
                  const t = computeVarietyTotal(d);
                  const buyerNames = (d.buyers || []).map(getBuyerName).filter(Boolean);
                  return (
                    <SummaryRow
                      key={v}
                      label={displayName}
                      sublabel={`हाथ ${Number(d.handQty) || 0} · कंबाइन ${Number(d.combQty) || 0}${buyerNames.length ? ` · खरीदार: ${buyerNames.join(", ")}` : ""
                        }`}
                      value={`${t.toLocaleString("en-IN")} बोरी`}
                      onClick={() => editVariety(idx)}
                      editable
                    />
                  );
                })}
              </Box>
            )}

            {screen === "done" && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", pt: "46px" }}>
                <Box
                  sx={{
                    width: 110,
                    height: 110,
                    borderRadius: "50%",
                    background: COLORS.emeraldSoft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: "22px",
                    animation: "mandiPop .5s cubic-bezier(.2,1.3,.4,1)",
                  }}
                >
                  <Box component="b" sx={{ fontSize: 60, color: COLORS.emerald }}>
                    ✓
                  </Box>
                </Box>
                <Box component="h2" sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: 25, margin: "0 0 6px" }}>
                  रिपोर्ट भेज दी गई!
                </Box>
                <Box component="p" sx={{ color: COLORS.muted, fontSize: "14.5px", lineHeight: 1.5, margin: "0 12px" }}>
                  धन्यवाद। आपकी आज की पूरी मंडी रिपोर्ट दर्ज हो गई है।
                </Box>

                <Box sx={{ width: "100%", mt: "22px", textAlign: "left" }}>
                  <SummaryRow label="पूरी मंडी आवक" value={`${totalMarket.toLocaleString("en-IN")} बोरी`} />

                  {selected.map((v) => {
                    const d = data[v] || makeEmptyVarietyData();
                    const displayName = getVarietyDisplayName(v, d);
                    const t = computeVarietyTotal(d);
                    const buyerCount = (d.buyers || []).map(getBuyerName).filter(Boolean).length;
                    return (
                      <SummaryRow
                        key={v}
                        label={displayName}
                        sublabel={`${buyerCount} खरीदार`}
                        value={`${t.toLocaleString("en-IN")} बोरी`}
                      />
                    );
                  })}
                </Box>

                <Box
                  sx={{
                    mt: "26px",
                    fontSize: 11.5,
                    color: COLORS.muted,
                    textAlign: "center",
                  }}
                >
                  © {new Date().getFullYear()} YTCTechventure. All rights reserved.
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <ScreenFooter>
          {screen === "partner" && (
            <Button
              fullWidth
              disableElevation
              onClick={() => goTo("total")}
              disabled={!canProceedFromPartner}
              sx={PRIMARY_BTN_SX}
            >
              आगे →
            </Button>
          )}

          {screen === "total" && (
            <Button
              fullWidth
              disableElevation
              onClick={handleToSelect}
              disabled={!isMarketTotalValid}
              sx={PRIMARY_BTN_SX}
            >
              आगे →
            </Button>
          )}

          {screen === "select" && (
            <Button
              fullWidth
              disableElevation
              onClick={startVarieties}
              disabled={selected.length === 0}
              sx={PRIMARY_BTN_SX}
            >
              आगे →
            </Button>
          )}

          {screen === "variety" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {hasRateError && (
                <Box sx={{ fontSize: 12, color: COLORS.amberWarn, fontWeight: 600, textAlign: "center" }}>
                  आगे बढ़ने से पहले रेट ठीक करें
                </Box>
              )}
              <Box sx={{ display: "flex", gap: "10px" }}>
                <Button
                  disableElevation
                  onClick={goBack}
                  sx={{ ...GHOST_BTN_SX, flex: "none", width: 58, fontSize: 22 }}
                >
                  ‹
                </Button>
                <Button
                  fullWidth
                  disableElevation
                  onClick={nextVariety}
                  disabled={hasRateError}
                  sx={PRIMARY_BTN_SX}
                >
                  {vIdx === selected.length - 1 ? "समीक्षा करें →" : "अगली वैरायटी →"}
                </Button>
              </Box>
            </Box>
          )}

          {screen === "preview" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {!duplicateSubmission && !isOk && (
                <Box sx={{ fontSize: 12, color: COLORS.amberWarn, fontWeight: 600, textAlign: "center" }}>
                  मिलान ठीक होने तक रिपोर्ट नहीं भेजी जा सकती
                </Box>
              )}
              {!duplicateSubmission && submitError && (
                <Box sx={{ fontSize: 12, color: COLORS.amberWarn, fontWeight: 600, textAlign: "center" }}>
                  {submitError}
                </Box>
              )}
              <Box sx={{ display: "flex", gap: "10px" }}>
                <Button
                  disableElevation
                  onClick={duplicateSubmission ? resetAll : goBack}
                  sx={
                    duplicateSubmission
                      ? { ...GHOST_BTN_SX, flex: "none", width: "auto", px: "18px" }
                      : { ...GHOST_BTN_SX, flex: "none", width: 58, fontSize: 22 }
                  }
                >
                  {duplicateSubmission ? "नई रिपोर्ट शुरू करें" : "‹"}
                </Button>
                {!duplicateSubmission && (
                  <Button
                    fullWidth
                    disableElevation
                    onClick={submitReport}
                    disabled={!isOk || submitting}
                    sx={PRIMARY_BTN_SX}
                  >
                    {submitting ? "भेजा जा रहा है…" : "रिपोर्ट भेजें ✓"}
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {screen === "done" && (
            <Button fullWidth disableElevation onClick={resetAll} sx={GHOST_BTN_SX}>
              नई रिपोर्ट
            </Button>
          )}
        </ScreenFooter>
      </Box>
    </>
  );
}